import { createOrgMutation, getMeQueryKey } from "@payezy/api-client/public/query";
import { Alert, Button, Card, Field, Input, cn } from "@payezy/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Briefcase, User } from "lucide-react";
import { useState } from "react";
import { Brand } from "../components/Brand";
import { problemMessage } from "../lib/api";
import { useMe } from "../lib/org";

export function WelcomePage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasPersonal = me.memberships.some((m) => m.org_type === "individual" && m.role_key === "owner");
  const [type, setType] = useState<"individual" | "business">(hasPersonal ? "business" : "individual");
  const [name, setName] = useState("");
  const create = useMutation({
    ...createOrgMutation(),
    onSuccess: async (org) => {
      await qc.invalidateQueries({ queryKey: getMeQueryKey() });
      navigate({ to: "/o/$orgId", params: { orgId: org.id } });
    },
  });

  const personalName = `${me.user.first_name} ${me.user.last_name}`.trim();

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-12">
      <Brand className="mb-8" />
      <Card className="w-full max-w-lg p-6">
        <h1 className="text-lg font-semibold">Open an account</h1>
        <p className="mt-1 text-sm text-ink-500">Choose the type of account. You can open more later.</p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          {(
            [
              { v: "individual", title: "Personal", desc: "For you as an individual", icon: <User className="size-5" />, disabled: hasPersonal },
              { v: "business", title: "Business", desc: "For your company and team", icon: <Briefcase className="size-5" />, disabled: false },
            ] as const
          ).map((o) => (
            <button
              key={o.v}
              type="button"
              disabled={o.disabled}
              onClick={() => setType(o.v)}
              className={cn(
                "cursor-pointer rounded-xl p-4 text-left ring-1 transition disabled:cursor-not-allowed disabled:opacity-40",
                type === o.v ? "bg-ink-950 text-white ring-ink-950" : "bg-white ring-ink-200 hover:ring-ink-400",
              )}
            >
              {o.icon}
              <p className="mt-3 text-sm font-semibold">{o.title}</p>
              <p className={cn("text-xs", type === o.v ? "text-ink-300" : "text-ink-500")}>{o.disabled ? "You already have one" : o.desc}</p>
            </button>
          ))}
        </div>
        <form
          className="mt-5 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ body: { type, name: type === "individual" ? name || personalName : name } });
          }}
        >
          {type === "business" ? (
            <Field label="Legal business name">
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Robotics, Inc." />
            </Field>
          ) : (
            <Field label="Account name" hint="Shown in your account switcher.">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={personalName || "Personal"} />
            </Field>
          )}
          {create.isError && <Alert tone="danger">{problemMessage(create.error)}</Alert>}
          <Button type="submit" loading={create.isPending}>Continue</Button>
        </form>
      </Card>
    </div>
  );
}
